// Taking your collected contacts somewhere else.
//
// A directory you cannot get out of is a directory you are locked into, and the
// people most likely to want this — somebody working a conference, a salesperson
// back from a week of meetings — need the cards in the tool they already use,
// not in another app. CSV because every CRM and every spreadsheet reads it.
//
// Built entirely on the device: the collection lives in IndexedDB and never goes
// to the server, so exporting it must not either.
import type { CollectedCard } from './collected';

/** Columns, in the order a person reading the file expects them. The private
 *  note comes last — it is the widest field and the one that would otherwise
 *  push everything useful off the first screen. */
const COLUMNS: { header: string; of: (c: CollectedCard) => string }[] = [
  { header: 'Name', of: (c) => c.name ?? '' },
  { header: 'Title', of: (c) => c.title ?? '' },
  { header: 'Company', of: (c) => c.company ?? '' },
  { header: 'Phone', of: (c) => c.phone ?? '' },
  { header: 'Email', of: (c) => c.email ?? '' },
  { header: 'Website', of: (c) => c.socials?.website ?? '' },
  { header: 'Address', of: (c) => c.address ?? '' },
  { header: 'Collected', of: (c) => isoDay(c.collectedAt) },
  { header: 'Card', of: (c) => (c.publicId ? `${location.origin}/${c.publicId}` : '') },
  // Space-free separator so a spreadsheet's own text-to-columns can split them.
  { header: 'Tags', of: (c) => (c.tags ?? []).join(';') },
  { header: 'Note', of: (c) => c.note ?? '' },
];

/** YYYY-MM-DD, which sorts correctly as text in every spreadsheet. */
function isoDay(ms: number | undefined): string {
  if (!ms) return '';
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Quote one CSV field (RFC 4180).
 *
 * A field is quoted when it contains a comma, a quote or a newline, and an
 * embedded quote is doubled. Notes are free text and routinely contain all
 * three, so getting this wrong shifts every later column into the wrong place
 * for that row — silently, in a file somebody imports into their CRM.
 */
function field(value: string): string {
  const v = value ?? '';
  // A leading =, +, - or @ makes a spreadsheet treat the cell as a FORMULA.
  // A note beginning "=" is not an attack here — the file never leaves the
  // device — but it is still the difference between reading your own words back
  // and reading #NAME?, so it is neutralised with a leading apostrophe.
  const safe = /^[=+\-@]/.test(v) ? `'${v}` : v;
  if (/[",\n\r]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

/** Render collected cards as an RFC 4180 CSV document. */
export function toCSV(cards: CollectedCard[]): string {
  const lines = [COLUMNS.map((c) => field(c.header)).join(',')];
  for (const card of cards) {
    lines.push(COLUMNS.map((c) => field(c.of(card))).join(','));
  }
  // CRLF, which is what RFC 4180 specifies and what Excel is happiest with.
  return lines.join('\r\n') + '\r\n';
}

/** A dated filename, so two exports do not overwrite each other in Downloads. */
export function csvFilename(now = new Date()): string {
  return `contacts-${now.toISOString().slice(0, 10)}.csv`;
}

/**
 * Hand the collection to the OS as a file.
 *
 * The BOM is not decoration. Excel opens a UTF-8 CSV as the local 8-bit
 * codepage unless the file starts with one, so without it every Bangla name in
 * the export arrives as mojibake — on the platform most likely to be opening it.
 */
export function downloadCSV(cards: CollectedCard[], now = new Date()): void {
  const blob = new Blob(['﻿', toCSV(cards)], { type: 'text/csv;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = csvFilename(now);
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}
